import { forwardRef, useState, useEffect, useRef } from "react";

import "./dropdown.scss";

function Dropdown(
  {
    options = [],
    children,
    className,
    classNameBtn,
    openLeft,
    showCaret,
    closeOnEscape = true,
  },
  ref,
) {
  const [showActions, setShowActions] = useState(false);
  const refDropdown = useRef();
  /**
   * The toggle button, for putting focus back on it when Escape closes the menu. The
   * forwarded `ref` already points at that button, but a caller may pass none (most do) or
   * pass a callback ref, so the component keeps its own handle and mirrors it outward —
   * see `setToggleRef`.
   */
  const refToggle = useRef(null);

  const setToggleRef = (node) => {
    refToggle.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (refDropdown.current && !refDropdown.current.contains(event.target)) {
        setShowActions(false);
      }
    };

    if (showActions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActions]);

  /**
   * ESCAPE CLOSES THE OPEN MENU, and the menu keeps the key.
   *
   * Added 2026-08-27. Until then this component's ENTIRE dismissal was the outside-`mousedown`
   * listener above: no Escape branch, no `onClose`, no imperative close. That forced a
   * workaround in KDS's section editor (`KcSectionCard`), where Escape means "discard this
   * section": with no way to ask an open menu to close, the card dispatched a synthetic
   * bubbling `mousedown` from its own root so every open menu would mistake it for a click
   * outside itself. That trick is gone now; this is the honest version of it.
   *
   * ---- WHY THE LISTENER IS ON `document`, IN THE CAPTURE PHASE ----
   *
   * `stopPropagation()` here has to stop an ANCESTOR'S React handler — that is the whole point
   * (Escape at an open menu must close the menu and must NOT also discard the section under
   * it). React attaches its listeners to the root container, so a `document` CAPTURE listener
   * runs before any of them and can keep the event away from every one: the innermost open
   * surface takes the key, which is the ordinary rule for nested dismissible things.
   *
   * A React `onKeyDown` on the wrapper would only see the key while focus is INSIDE the
   * dropdown. That is the common case (clicking the trigger focuses it) but not the only one —
   * a Tab away from the trigger moves focus without closing the menu, since nothing pressed
   * the mouse — and in that case the ancestor would have taken the key. `document` matches
   * where the existing dismissal already listens.
   *
   * `preventDefault()` as well as `stopPropagation()`: a handler that checks
   * `event.defaultPrevented` (KDS's card does, for the shared editor's link dialog) then reads
   * this as already dealt with even if it somehow sees the event.
   *
   * Bound only while the menu is open, so a closed Dropdown is invisible to the keyboard and
   * cannot swallow an Escape meant for anything else. That gate is what keeps the cost of
   * capturing at `document` small, and the cost is real and worth naming: WHILE A MENU IS OPEN
   * this takes the key from `document`-level Escape listeners too — the shared `Popover`, the
   * shared `Alert`, an error `ConfirmModal`, and about ten consumer-local ones. Reaching that
   * state takes two dismissible surfaces open at once, which a pointer cannot produce (the
   * click that opens a menu is the outside-`mousedown` that closes whatever else was open), and
   * where it can be reached the menu is the innermost of the two and should have the key.
   *
   * `closeOnEscape={false}` opts a caller out entirely, for a Dropdown whose Escape genuinely
   * belongs to something above it.
   */
  useEffect(() => {
    if (!showActions || !closeOnEscape) return;

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setShowActions(false);
      // Back to the trigger: the key was pressed at the menu, so focus belongs on the thing
      // that opened it and not wherever the browser would drop it once the menu unmounts.
      refToggle.current?.focus();
    };

    document.addEventListener("keydown", handleEscape, true);
    return () => document.removeEventListener("keydown", handleEscape, true);
  }, [showActions, closeOnEscape]);

  return (
    <div
      className={`dropdown ${className || ""} ${openLeft ? "open-left" : ""}`}
      ref={refDropdown}
    >
      <button
        ref={setToggleRef}
        className={`dropdown-toggle ${classNameBtn || ""}`}
        onClick={(e) => {
          e.stopPropagation();
          setShowActions((value) => !value);
        }}
      >
        {children}
        {showCaret &&
          (showActions ? (
            <span className="material-symbols-outlined no-margin">
              expand_less
            </span>
          ) : (
            <span className="material-symbols-outlined no-margin">
              expand_more
            </span>
          ))}
      </button>
      {showActions && (
        <div className="dropdown-menu">
          {options.map(
            (option, index) =>
              option.show !== false && (
                <div
                  key={index}
                  className={`dropdown-item${option.onClick ? " clickable" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowActions(false);
                    if (option.onClick) option.onClick();
                  }}
                >
                  {option.icon && (
                    <span className="material-symbols-outlined">
                      {option.icon}
                    </span>
                  )}
                  {option.label}
                </div>
              ),
          )}
        </div>
      )}
    </div>
  );
}

export default forwardRef(Dropdown);
